import React from "react";
import * as Icons from "lucide-react";
import { ArrowRight, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import { VALUE_COSTS, PERSONAS, FAQS } from "@/data/mock";
import { useStartFlow } from "@/lib/useStart";

export const ValueAnchor = () => (
  <section className="bg-[#040406] text-white py-24 md:py-32" data-testid="value-anchor-section">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
      <Reveal>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
          What does one month of confused preparation cost?
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3">
          {VALUE_COSTS.map((c) => (
            <div key={c} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3.5">
              <X className="h-4 w-4 text-red-400 shrink-0" />
              <span className="text-sm text-white/70">{c}</span>
            </div>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-12">
          <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            The real cost isn't ₹1,999.
          </h3>
          <h3 className="mt-2 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white/50">
            It's another month spent preparing without knowing whether you're improving.
          </h3>
        </div>
      </Reveal>
    </div>
  </section>
);

export const WhoItsFor = () => (
  <section className="bg-background py-24 md:py-32" data-testid="who-its-for-section">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-2xl mb-14">
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">Who it's for</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
          Built for serious aspirants.
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {PERSONAS.map((p, i) => {
          const Icon = Icons[p.icon] || Icons.User;
          return (
            <Reveal key={p.title} delay={i * 0.07}>
              <div className="h-full rounded-2xl border border-border bg-card p-7 hover:-translate-y-1 hover:shadow-lg transition-all">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-400/20 to-red-600/20 flex items-center justify-center mb-6">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-display text-sm font-bold tracking-wide mb-3">{p.title}</h3>
                <p className="text-sm text-muted-foreground italic leading-relaxed">"{p.quote}"</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const FAQSection = () => (
  <section id="faq" className="bg-background py-24 md:py-32 scroll-mt-16" data-testid="faq-section">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
      <Reveal className="text-center mb-14">
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">FAQ</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
          Questions, answered honestly.
        </h2>
      </Reveal>
      <Reveal>
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-border">
              <AccordionTrigger className="text-left font-display font-medium text-base hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const FinalCTA = () => {
  const start = useStartFlow();
  return (
    <section className="relative overflow-hidden bg-[#040406] text-white py-28 md:py-36" data-testid="final-cta-section">
      <div className="absolute inset-0 hero-radial" />
      <div className="absolute inset-0 grid-lines opacity-30" />
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-4xl">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
            Your competition isn't waiting.
            <br />
            Your preparation shouldn't either.
          </h2>
          <p className="mt-6 text-lg text-white/60">Stop wondering what to study next.</p>
          <p className="mt-8 font-display text-2xl sm:text-3xl font-bold text-gradient">
            Know. Practice. Improve. Repeat.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <button
            onClick={() => start("final-cta")}
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-red-600 text-white font-medium px-8 py-4 text-lg shadow-[0_0_35px_rgba(250,204,21,0.4)] hover:scale-105 transition-transform"
            data-testid="final-cta-button"
          >
            Start Your sarkarijobai Journey <ArrowRight className="h-5 w-5" />
          </button>
          <p className="mt-6 text-sm text-white/40">Choose your plan · Secure checkout · Guarantee terms apply</p>
        </Reveal>
      </div>
    </section>
  );
};
